export const hasOwn = Object.hasOwn ? Object.hasOwn.bind(Object) : Object.prototype.hasOwnProperty.call
export const isObject = obj => Boolean(obj && 'object' === typeof obj && !Array.isArray(obj))

export function deepMergeObject(target = {}, source = {}) {
  for (const key in source) {
    if (hasOwn(source, key)) {
      const sourceValue = source[key]
      target[key] =
        isObject(sourceValue) && !Array.isArray(sourceValue)
          ? deepMergeObject(target[key] || {}, sourceValue)
          : sourceValue
    }
  }
  return target
}

export function deepMergeCloneObject(target = {}, source = {}) {
  const targetClone = {}
  for (const key in source) {
    if (hasOwn(source, key)) {
      const sourceValue = source[key]
      const targetValue = target[key]
      if (Array.isArray(sourceValue)) {
        targetClone[key] = deepClone(sourceValue)
      } else if (
        null != sourceValue &&
        null != targetValue &&
        'object' === typeof sourceValue &&
        'object' === typeof targetValue
      ) {
        targetClone[key] = deepMergeCloneObject(targetValue, sourceValue)
      } else {
        targetClone[key] = deepClone(sourceValue)
      }
    }
  }
  for (const key in target) {
    if (hasOwn(target, key) && 'undefined' === typeof targetClone[key]) {
      targetClone[key] = deepClone(target[key])
    }
  }
  return targetClone
}

export function clearObject(target) {
  if ('object' !== typeof target || null === target) {
    return target
  }
  for (const key in target) {
    if (hasOwn(target, key)) {
      delete target[key]
    }
  }
  return target
}

export function getIn(target, path, fallbackValue) {
  if (!Array.isArray(path) || 0 === path.length) {
    return 'function' === typeof fallbackValue ? fallbackValue(target, path) : fallbackValue
  }

  if ('object' !== typeof target || null === target) {
    return 'function' === typeof fallbackValue ? fallbackValue(target, path) : fallbackValue
  }

  let current = target

  for (const key of path) {
    if ('undefined' === typeof current[key]) {
      return 'function' === typeof fallbackValue ? fallbackValue(target, path) : fallbackValue
    }
    current = current[key]
  }

  return current
}

export function deepEqual(obj1, obj2) {
  if (obj1 === obj2) {
    return true
  }

  const obj1IsArray = Array.isArray(obj1)
  const obj2IsArray = Array.isArray(obj2)

  if (obj1IsArray && obj2IsArray) {
    if (obj1.length !== obj2.length) {
      return false
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) {
        return false
      }
    }
    return true
  } else if ((obj1IsArray && !obj2IsArray) || (obj2IsArray && !obj1IsArray)) {
    return false
  }

  if (null === obj1 && null === obj2) {
    return true
  }
  if ('object' !== typeof obj1 && 'object' !== typeof obj2) {
    return obj1 === obj2
  }

  if ('object' !== typeof obj1 || 'object' !== typeof obj2 || null === obj1 || null === obj2) {
    return false
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false
    }
  }

  return true
}

export function shallowEqual(obj1, obj2) {
  if (obj1 === obj2) {
    return true
  }
  const obj1IsArray = Array.isArray(obj1)
  const obj2IsArray = Array.isArray(obj2)
  if (obj1IsArray && obj2IsArray) {
    if (obj1.length !== obj2.length) {
      return false
    }
    for (let i = 0; i < obj1.length; i++) {
      if (obj1[i] !== obj2[i]) {
        return false
      }
    }
    return true
  }

  if ((obj1IsArray && !obj2IsArray) || (obj2IsArray && !obj1IsArray)) {
    return false
  }

  if (null === obj1 && null === obj2) {
    return true
  }

  if ('object' !== typeof obj1 && 'object' !== typeof obj2) {
    return obj1 === obj2
  }

  if ('object' !== typeof obj1 || 'object' !== typeof obj2 || null === obj1 || null === obj2) {
    return false
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || obj1[key] !== obj2[key]) {
      return false
    }
  }

  return true
}

export function isSubset(subsetObject, mainObject) {
  if (subsetObject === mainObject) {
    return true
  }
  if ('object' !== typeof subsetObject || 'object' !== typeof mainObject) {
    return false
  }
  if (null === subsetObject || null === mainObject) {
    return false
  }
  const subsetKeys = Object.keys(subsetObject)
  for (const key of subsetKeys) {
    if (!hasOwn(mainObject, key) || subsetObject[key] !== mainObject[key]) {
      return false
    }
  }
  return true
}

function deepCloneCircularSafe(obj, seen) {
  if (null == obj || 'object' !== typeof obj) {
    return obj
  }

  if (seen.has(obj)) {
    return seen.get(obj)
  }

  if (Array.isArray(obj)) {
    const clonedObject = obj.map(item => deepCloneCircularSafe(item, seen))
    seen.set(obj, clonedObject)
    return clonedObject
  } else {
    const clonedObject = {}
    seen.set(obj, clonedObject)
    for (const key in obj) {
      if (hasOwn(obj, key)) {
        clonedObject[key] = deepCloneCircularSafe(obj[key], seen)
      }
    }
    return clonedObject
  }
}
export function deepClone(obj) {
  return deepCloneCircularSafe(obj, new WeakMap())
}

function deepFreezeCircularSafe(obj, seen) {
  if ('object' !== typeof obj || null === obj || Object.isFrozen(obj)) {
    return obj
  }

  if (seen.has(obj)) {
    return obj
  }
  seen.add(obj)

  for (const key in obj) {
    if (hasOwn(obj, key)) {
      obj[key] = deepFreezeCircularSafe(obj[key], seen)
    }
  }
  return Object.freeze(obj)
}
export function deepFreeze(obj) {
  return deepFreezeCircularSafe(obj, new WeakSet())
}
